import { Column, EditType } from '@src/model/table';
import { Service } from '@src/dtos/version.dto';
import React from 'react';
import { getCustomEnumOptions, ProtocolType, ServiceActionType } from '@src/utils/enums';
import { ServiceRuleCell } from './service-rule-cell';
import { ServicePortsCell } from './service-port-cell';
import {
    convertAndCheckPorts,
    isValueDuplicate,
    maxLength,
    nameValidation
} from '@src/utils/validationUtils';
import { DisplayNameCell } from '../inventory-utils';
import { isTCPOrUDPAllService } from '@src/utils/direction-utils';
import { toCamelCase } from '@src/utils/string-normalization';
import { createDescriptionColumn } from '@src/components/shared/table/column-factories';

export const createServiceColumns = (specialServices: string[]): Column<Service>[] => {
    const isTCPOrUDP = (displayName: string) => isTCPOrUDPAllService(displayName, specialServices);

    const disableEdit = (row: Service) => row.isDefault || isTCPOrUDP(row.displayName);

    const getServiceStateOptions = (row: Service) =>
        getCustomEnumOptions(ServiceActionType, isTCPOrUDP(row.displayName) ? ServiceActionType.BLOCK : undefined)
            .map(option => option.label === ServiceActionType.NOT_APPLY
                ? { label: 'Not Apply', value: option.value }
                : { label: toCamelCase(option.label), value: option.value });

    return [
        {
            accessor: 'displayName',
            header: 'Name',
            width: '200px',
            displayOnEdit: true,
            type: EditType.Text,
            getPlaceholder: () => 'Service Name',
            maxLength: 25,
            validationRules: [maxLength(25), isValueDuplicate,
                (value) => nameValidation(value)],
            disableEdit,
            mandatory: (row: Service) => !row.isDefault,
            getDisplayElement: (column, row) => <DisplayNameCell displayName={row.displayName} />,
            description: 'Enter at least one source or destination port. Numbers or ranges, comma separated (e.g. 80-90, 45, 1212) ',
        },
        {
            accessor: 'proto',
            header: 'Protocol',
            width: '120px',
            displayOnEdit: true,
            type: EditType.Dropdown,
            getPlaceholder: () => 'Select',
            options: () => getCustomEnumOptions(ProtocolType),
            disableEdit,
            isKeyField: true,
            mandatory: (row: Service) => !row.isDefault
        },
        {
            accessor: 'sports',
            header: 'Port',
            width: '350px',
            displayOnEdit: true,
            type: EditType.Text,
            getPlaceholder: () => 'Numbers or ranges, comma separated ',
            getDisplayElement: (column, row) => <ServicePortsCell destinationPortsData={row.dports} sourcePortsData={row.sports}/>,
            validationRules: [maxLength(1000), convertAndCheckPorts],
            description: 'Default behaviour',
            isDescriptionHidden: true,
            isKeyField: true,
            disableEdit,
            mandatory: (row: Service) => !row.dports?.length && !row.isDefault
        },
        {
            accessor: 'dports',
            header: 'Destination Port',
            width: '200px',
            displayOnEdit: true,
            type: EditType.Text,
            getPlaceholder: () => '85, 45-56, 1210-1310',
            validationRules: [maxLength(1000), convertAndCheckPorts],
            mandatory: (row: Service) => !row.sports?.length && !row.isDefault,
            disableEdit,
            isKeyField: true,
            isHidden: true
        },
        {
            accessor: 'serviceStateInbound',
            header: 'Inbound',
            width: '200px',
            displayOnEdit: true,
            type: EditType.Dropdown,
            getPlaceholder: () => 'Select',
            options: getServiceStateOptions,
            mandatory: true,
            isHidden: true
        },
        {
            accessor: 'serviceStateOutbound',
            header: 'Outbound',
            width: '200px',
            displayOnEdit: true,
            type: EditType.Dropdown,
            getPlaceholder: () => 'Select',
            options: getServiceStateOptions,
            mandatory: true,
            isHidden: true
        },
        {
            accessor: 'id',
            header: 'Rules',
            width: '350px',
            displayOnEdit: false,
            type: EditType.Text,
            getDisplayElement: (column, row) => <ServiceRuleCell serviceData={row}/>
        },
        {
            ...createDescriptionColumn(400),
            disableEdit,
        }
    ];
};
